import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type ExecutionEvidenceResponse,
  type ExecutionEvidenceSource,
  type ExecutionStepRow,
  type StepEvidence,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { AuthedBrand } from './AuthedBrand';
import { journeyTypeLabel } from './journeyLabels';
import { describeOutcome, type OutcomeTone } from './executionOutcome';
import styles from './JourneyExecutionEvidence.module.css';

type LoadState = 'loading' | 'error' | 'ready';

export interface JourneyExecutionEvidenceProps {
  client?: ApiClient;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

const BANNER_TONE: Record<OutcomeTone, string | undefined> = {
  success: styles.bannerSuccess,
  danger: styles.bannerDanger,
  warning: styles.bannerWarning,
  neutral: styles.bannerNeutral,
};

function isFailed(row: ExecutionStepRow): boolean {
  return row.outcome === 'failed';
}

/** Narrow the raw `?source=` query param to the closed set (#250); anything else
 * (including absent) is undefined — a legacy all-sources lookup. */
function narrowSource(raw: string | null): ExecutionEvidenceSource | undefined {
  return raw === 'discovered' || raw === 'customer-provided' ? raw : undefined;
}

/**
 * Project journey EXECUTION-evidence drill-down (#127). Deep-linkable view of the
 * latest terminal run for ONE journey type: its outcome, the per-step grid, and —
 * for steps with a real evidence row — the redacted screenshot, console, and
 * network errors captured by #28. Honest by construction: the defect label is
 * derived client-side and shown ONLY for a failed assertion; every other failure
 * is explicitly "not a product defect". Expired evidence shows an honest
 * placeholder; a null screenshot shows "No screenshot captured" while keeping the
 * text/logs. A manual Refresh re-fetches (no polling).
 */
export function JourneyExecutionEvidence({
  client = defaultClient,
}: JourneyExecutionEvidenceProps) {
  const { currentOrg } = useOrg();
  const { id, journeyType } = useParams<{ id: string; journeyType: string }>();
  const [searchParams] = useSearchParams();
  useFocusMain();

  const orgId = currentOrg?.id;
  const type = journeyType ?? '';
  const source = narrowSource(searchParams.get('source'));

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [data, setData] = useState<ExecutionEvidenceResponse | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    if (!orgId || !id || !type) return;
    setPhase('loading');
    try {
      const next = await client.executionEvidence(orgId, id, type, source);
      setData(next);
      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id, type, source]);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    if (!orgId || !id || !type || refreshing) return;
    setRefreshing(true);
    try {
      const next = await client.executionEvidence(orgId, id, type, source);
      setData(next);
      setStatusMessage('Run evidence updated.');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    } finally {
      setRefreshing(false);
    }
  }, [client, orgId, id, type, source, refreshing]);

  const label = journeyTypeLabel(type);
  const backHref = `/projects/${id ?? ''}/journey-health`;

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <p className={styles.back}>
          <Link to={backHref}>Back to journey health</Link>
        </p>

        <h1 id="jee-heading" className={styles.heading}>
          {label} — run evidence
        </h1>
        <p className={styles.subheading}>
          The outcome and captured evidence of this journey&rsquo;s latest completed run. Evidence
          is shown only when a real run produced it, and the tool never decides whether a failure is
          a product defect — it reports; you decide.
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading} role="status" aria-live="polite">
            <Spinner />
            <span>Loading run evidence…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Link to="/" className={styles.backLink}>
              Back to home
            </Link>
          </div>
        ) : null}

        {phase === 'ready' && data ? (
          <>
            <InlineAlert severity="success">{statusMessage}</InlineAlert>

            <div className={styles.actions}>
              <Button variant="secondary" busy={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>

            {data.state === 'no-data' ? (
              <div className={styles.panel}>
                <InlineAlert severity="info">
                  This journey has no completed validation run yet. Run a scan to validate it.{' '}
                  <Link to={`/projects/${id ?? ''}/scans`}>Go to scans</Link>
                </InlineAlert>
              </div>
            ) : null}

            {data.state === 'in-progress' ? (
              <div className={styles.panel}>
                <InlineAlert severity="info">
                  This journey&rsquo;s latest run is still in progress. Evidence appears once the run
                  reaches a terminal outcome.
                </InlineAlert>
              </div>
            ) : null}

            {data.state === 'ok' ? (
              <>
                <div className={styles.panel}>
                  <InlineAlert severity="info">
                    <strong>What you&rsquo;re seeing.</strong> This is the result of TestCraft
                    actually running the journey like a shopper &mdash; what passed or failed, and
                    the evidence captured along the way (screenshots are redacted). Unlike the
                    discovery view, this is a real run outcome. TestCraft reports what it saw;
                    whether a failure is a real product defect is your call.
                  </InlineAlert>
                </div>
                <OkView data={data} />
              </>
            ) : null}
          </>
        ) : null}
      </Card>
    </AppShell>
  );
}

function OkView({ data }: { data: Extract<ExecutionEvidenceResponse, { state: 'ok' }> }) {
  const { run, steps, windowDays } = data;
  const outcome = describeOutcome(run.outcome, run.reasonCode);
  const started = formatTime(run.startedAt);
  const finished = formatTime(run.completedAt ?? run.cancelledAt);
  const evidencedSteps = steps.filter((s) => s.evidence.kind !== 'none');

  return (
    <>
      <section className={`${styles.banner} ${BANNER_TONE[outcome.tone]}`} aria-labelledby="jee-outcome">
        <h2 id="jee-outcome" className={styles.bannerHeadline}>
          {outcome.headline}
        </h2>
        <p className={styles.rawCodes}>
          Outcome: {run.outcome ?? '—'} · Reason: {run.reasonCode ?? '—'}
        </p>
        <p className={styles.qualifier}>{outcome.qualifier}</p>
        <p className={styles.runMeta}>
          <span>Run {run.runId}</span>
          {started ? (
            <>
              <span aria-hidden="true">·</span>
              <span>Started {started}</span>
            </>
          ) : null}
          {finished ? (
            <>
              <span aria-hidden="true">·</span>
              <span>Ended {finished}</span>
            </>
          ) : null}
        </p>
      </section>

      <table className={styles.table} aria-labelledby="jee-heading">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Action</th>
            <th scope="col">Classification</th>
            <th scope="col">Outcome</th>
            <th scope="col">Reason</th>
            <th scope="col">HTTP</th>
            <th scope="col">Errors</th>
            <th scope="col">Phase</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((step) => (
            <tr key={step.stepResultId} className={isFailed(step) ? styles.rowFailed : undefined}>
              <th scope="row">
                {isFailed(step) ? <span className={styles.srOnly}>Failed step: </span> : null}
                {step.stepOrder}
              </th>
              <td>{step.actionKind}</td>
              <td>{step.classification}</td>
              <td>
                <StatusBadge kind="execution" status={step.outcome} />
              </td>
              <td className={styles.mono}>{step.reasonCode || '—'}</td>
              <td>{step.httpStatus ?? '—'}</td>
              <td>{step.errorCount}</td>
              <td>
                <span className={step.phase === 'cleanup' ? styles.phaseCleanup : styles.phaseForward}>
                  {step.phase === 'cleanup' ? 'Cleanup (teardown)' : 'Forward'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {evidencedSteps.length > 0 ? (
        <div className={styles.evidenceList}>
          {evidencedSteps.map((step) => (
            <EvidenceSection key={step.stepResultId} step={step} windowDays={windowDays} />
          ))}
        </div>
      ) : null}
    </>
  );
}

function EvidenceSection({ step, windowDays }: { step: ExecutionStepRow; windowDays: number }) {
  const headingId = `step-${step.stepOrder}-heading`;
  const ev = step.evidence;
  return (
    <section
      id={`step-${step.stepOrder}-evidence`}
      className={styles.evidence}
      aria-labelledby={headingId}
      tabIndex={-1}
    >
      <h3 id={headingId} className={styles.evidenceHeading}>
        Step {step.stepOrder}: {step.actionKind}
      </h3>
      <EvidenceBody ev={ev} step={step} windowDays={windowDays} />
    </section>
  );
}

function EvidenceBody({
  ev,
  step,
  windowDays,
}: {
  ev: StepEvidence;
  step: ExecutionStepRow;
  windowDays: number;
}) {
  if (ev.kind === 'expired') {
    const asOf = formatTime(ev.expiredAsOf);
    return (
      <InlineAlert severity="warning">
        Evidence expired — captured more than {windowDays} days ago, outside your retention window.
        {asOf ? ` Aged out ${asOf}.` : ''}
      </InlineAlert>
    );
  }

  if (ev.kind === 'none') return null;

  return (
    <>
      {ev.kind === 'full' ? (
        <img
          className={styles.screenshot}
          src={ev.screenshotDataUrl}
          alt={`Redacted screenshot of failed step ${step.stepOrder}: ${step.actionKind}`}
          loading="lazy"
          decoding="async"
        />
      ) : (
        <p className={styles.noShot}>
          No screenshot captured for this step. Console and network evidence is shown below.
        </p>
      )}

      {ev.pageUrl ? <p className={styles.pageUrl}>Captured on: {ev.pageUrl}</p> : null}

      <h4 className={styles.evidenceSubheading}>Console errors</h4>
      {ev.consoleLogs.length > 0 ? (
        <ul className={styles.logList}>
          {ev.consoleLogs.map((log, i) => (
            <li key={`${step.stepResultId}-console-${i}`}>
              <span className={styles.logLevel}>{log.level || 'log'}</span>
              <span className={styles.mono}>{log.message}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.metaMuted}>No console errors captured.</p>
      )}

      <h4 className={styles.evidenceSubheading}>Network errors</h4>
      {ev.networkErrors.length > 0 ? (
        <table className={styles.netTable}>
          <thead>
            <tr>
              <th scope="col">Method</th>
              <th scope="col">URL</th>
              <th scope="col">Status</th>
              <th scope="col">Detail</th>
            </tr>
          </thead>
          <tbody>
            {ev.networkErrors.map((net, i) => (
              <tr key={`${step.stepResultId}-net-${i}`}>
                <td>{net.method || '—'}</td>
                <td className={styles.mono}>{net.url}</td>
                <td>{net.status ?? '—'}</td>
                <td>{net.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={styles.metaMuted}>No network errors captured.</p>
      )}

      <p className={styles.redactionNote}>
        <span aria-hidden="true">🛡 </span>
        Redacted before storage · {ev.redactionMeta.fieldsMasked} field(s) masked ·{' '}
        {ev.redactionMeta.tokensStripped} token(s) stripped · strategy{' '}
        {ev.redactionMeta.strategyVersion || 'n/a'}
        {formatTime(ev.capturedAt) ? ` · Captured ${formatTime(ev.capturedAt)}` : ''}
      </p>
    </>
  );
}
